import { useState, useEffect } from 'react';
import useTripStore from '../../context/TripStore';

const TurnByTurnDirections = () => {
  const { trip, places, itinerary, navigation, settings } = useTripStore();
  const activePlan = itinerary.activePlan === 'a' ? itinerary.planA : itinerary.planB;
  const { isActive, currentStopIndex } = navigation || {};
  const travelMode = settings?.travelMode || 'WALKING';
  
  const [directions, setDirections] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  useEffect(() => {
    if (!isActive || !activePlan?.stops || currentStopIndex === undefined) {
      setDirections(null);
      return;
    }

    const currentStop = activePlan.stops[currentStopIndex];
    if (!currentStop) return;

    // Determine origin and destination
    let origin = null;
    let destination = null;

    if (currentStopIndex === 0) {
      // First stop: route from start location
      origin = trip.startLocation?.coordinates;
      const currentPlace = places.find(p => p.name === currentStop.name || p.placeId === currentStop.placeId);
      destination = currentPlace?.coordinates;
    } else {
      // Subsequent stops: route from previous stop
      const previousStop = activePlan.stops[currentStopIndex - 1];
      const previousPlace = places.find(p => p.name === previousStop.name || p.placeId === previousStop.placeId);
      const currentPlace = places.find(p => p.name === currentStop.name || p.placeId === currentStop.placeId);
      
      origin = previousPlace?.coordinates;
      destination = currentPlace?.coordinates;
    }

    if (!origin || !destination || !window.google?.maps?.DirectionsService) {
      setDirections(null);
      return;
    }

    setLoading(true);
    setError(null);

    const directionsService = new window.google.maps.DirectionsService();
    
    const routeRequest = {
      origin: origin,
      destination: destination,
      travelMode: window.google.maps.TravelMode[travelMode],
    };

    // Add mode-specific options
    if (travelMode === 'WALKING') {
      routeRequest.avoidHighways = true;
      routeRequest.avoidFerries = true;
    } else if (travelMode === 'BICYCLING') {
      routeRequest.avoidHighways = true;
      routeRequest.avoidFerries = true;
    } else if (travelMode === 'TRANSIT') {
      // For transit, use current time for real-time schedules
      routeRequest.transitOptions = {
        departureTime: new Date(),
        modes: ['BUS', 'SUBWAY', 'TRAIN', 'TRAM', 'RAIL'], // Include all transit modes
        routingPreference: 'LESS_WALKING', // Prefer routes with less walking
      };
    }

    directionsService.route(routeRequest, (result, status) => {
      setLoading(false);
      if (status === 'OK' && result.routes && result.routes[0]) {
        const route = result.routes[0];
        const leg = route.legs[0];
        
        // Process steps with transit information
        const processedSteps = leg.steps.map((step, index) => {
          const baseStep = {
            index: index + 1,
            instruction: step.instructions.replace(/<[^>]*>/g, ''), // Remove HTML tags
            distance: step.distance.text,
            duration: step.duration.text,
            travelMode: step.travel_mode,
          };

          // Add transit-specific information
          if (step.travel_mode === 'TRANSIT' && step.transit) {
            const transit = step.transit;
            baseStep.transit = {
              line: transit.line,
              name: transit.line?.short_name || transit.line?.name || 'Transit',
              vehicle: transit.line?.vehicle || { type: 'TRANSIT' },
              departureStop: transit.departure_stop?.name || 'Transit Stop',
              arrivalStop: transit.arrival_stop?.name || 'Transit Stop',
              numStops: transit.num_stops || 0,
              headsign: transit.headsign || '',
            };
          }

          return baseStep;
        });
        
        setDirections({
          distance: leg.distance.text,
          duration: leg.duration.text,
          steps: processedSteps,
        });
        setCurrentStepIndex(0); // Reset to first step when new directions load
      } else {
        setError(`Unable to get directions: ${status}`);
        setDirections(null);
      }
    });
  }, [isActive, currentStopIndex, activePlan, places, trip.startLocation, travelMode]);

  if (!isActive || !directions) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 p-4 rounded mb-4">
        <p className="text-xs font-light text-gray-500">Loading directions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 p-4 rounded mb-4">
        <p className="text-xs font-light text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 p-4 rounded mb-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-light mb-1">Directions</h3>
          <div className="flex items-center gap-3 text-xs font-light text-gray-600">
            <span>{directions.distance}</span>
            <span>•</span>
            <span>{directions.duration}</span>
          </div>
        </div>
        <div className="text-xs font-light text-gray-500">
          {travelMode === 'WALKING' ? '🚶' : travelMode === 'TRANSIT' ? '🚇' : travelMode === 'DRIVING' ? '🚗' : '🚴'}
        </div>
      </div>

      {/* Current step - prominent */}
      {directions.steps.length > 0 && (() => {
        const currentStep = directions.steps[currentStepIndex];
        const isTransit = currentStep?.travelMode === 'TRANSIT';
        const isWalking = currentStep?.travelMode === 'WALKING';
        
        return (
          <div className="bg-gray-900 text-white p-4 rounded mb-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-light text-gray-300">Next Instruction</div>
              {isTransit && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-600 rounded text-xs font-light">
                  {currentStep.transit?.vehicle?.type === 'SUBWAY' ? '🚇' : 
                   currentStep.transit?.vehicle?.type === 'HEAVY_RAIL' ? '🚇' :
                   currentStep.transit?.vehicle?.type === 'METRO_RAIL' ? '🚇' :
                   currentStep.transit?.vehicle?.type === 'BUS' ? '🚌' : '🚇'}
                  <span>{currentStep.transit?.name}</span>
                </div>
              )}
              {isWalking && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-green-600 rounded text-xs font-light">
                  🚶 Walk
                </div>
              )}
            </div>
            
            {isTransit && currentStep.transit && (
              <div className="mb-2 pb-2 border-b border-gray-700">
                <div className="text-xs font-light text-gray-300 mb-1">
                  Board at <span className="text-white font-medium">{currentStep.transit.departureStop}</span>
                </div>
                <div className="text-xs font-light text-gray-300">
                  Get off at <span className="text-white font-medium">{currentStep.transit.arrivalStop}</span>
                  {currentStep.transit.numStops > 0 && (
                    <span className="ml-1">({currentStep.transit.numStops} {currentStep.transit.numStops === 1 ? 'stop' : 'stops'})</span>
                  )}
                </div>
                {currentStep.transit.headsign && (
                  <div className="text-xs font-light text-gray-400 mt-1">
                    Towards: {currentStep.transit.headsign}
                  </div>
                )}
              </div>
            )}
            
            <p className="text-sm font-light mb-2">{currentStep.instruction}</p>
            <div className="flex items-center gap-3 text-xs font-light text-gray-300">
              <span>{currentStep.distance}</span>
              <span>•</span>
              <span>{currentStep.duration}</span>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setCurrentStepIndex(Math.max(0, currentStepIndex - 1))}
                disabled={currentStepIndex === 0}
                className="text-xs py-1 px-3 bg-white bg-opacity-20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              <button
                onClick={() => setCurrentStepIndex(Math.min(directions.steps.length - 1, currentStepIndex + 1))}
                disabled={currentStepIndex === directions.steps.length - 1}
                className="text-xs py-1 px-3 bg-white bg-opacity-20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        );
      })()}

      {/* All steps list */}
      <div className="space-y-2 max-h-48 overflow-y-auto">
        <div className="text-xs font-light text-gray-600 mb-2">All Steps ({directions.steps.length})</div>
        {directions.steps.map((step, index) => {
          const isTransit = step.travelMode === 'TRANSIT';
          const isWalking = step.travelMode === 'WALKING';
          
          return (
            <div 
              key={index}
              className={`
                flex gap-3 p-2 rounded transition-colors cursor-pointer
                ${index === currentStepIndex ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'}
              `}
              onClick={() => setCurrentStepIndex(index)}
            >
              <div className={`
                flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-light
                ${index === currentStepIndex ? 'bg-white text-gray-900' : isTransit ? 'bg-blue-100 text-blue-700' : isWalking ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}
              `}>
                {isTransit ? '🚇' : isWalking ? '🚶' : step.index}
              </div>
              <div className="flex-1 min-w-0">
                {isTransit && step.transit && (
                  <div className={`mb-1 ${index === currentStepIndex ? 'text-gray-300' : 'text-gray-600'}`}>
                    <div className="flex items-center gap-2 text-xs font-light">
                      <span className={`px-1.5 py-0.5 rounded ${index === currentStepIndex ? 'bg-blue-600' : 'bg-blue-100 text-blue-700'}`}>
                        {step.transit.name}
                      </span>
                      {step.transit.numStops > 0 && (
                        <span className="text-xs">
                          {step.transit.numStops} {step.transit.numStops === 1 ? 'stop' : 'stops'}
                        </span>
                      )}
                    </div>
                    <div className="text-xs mt-0.5">
                      {step.transit.departureStop} → {step.transit.arrivalStop}
                    </div>
                  </div>
                )}
                <p className={`text-xs font-light mb-0.5 ${index === currentStepIndex ? 'text-white' : 'text-gray-900'}`}>
                  {step.instruction}
                </p>
                <div className={`flex items-center gap-2 text-xs font-light ${index === currentStepIndex ? 'text-gray-300' : 'text-gray-500'}`}>
                  <span>{step.distance}</span>
                  <span>•</span>
                  <span>{step.duration}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TurnByTurnDirections;

